import { useRevalidate } from "@/lib/loader";
import { useEffect, useState, useTransition } from "react";
import type { Invitation, Workspace } from "@/lib/api/types";
import { listMyInvitations } from "@/lib/api/endpoints";
import { acceptInvitationAction } from "@/lib/api/invitations";
import { writeActiveCustomerCookie } from "@/lib/mapper/active-customer-cookie";
import { clearApiCache } from "@/lib/api/client";

function roleLabel(role: Workspace["role"]): string {
  switch (role) {
    case "OWNER":
      return "Owner";
    case "ADMIN":
      return "Admin";
    default:
      return "Member";
  }
}

/**
 * Active-workspace switcher. Lists the workspaces the current user is a member
 * of (their memberships) and writes the chosen one to the `v2_active_customer`
 * cookie — which the API client sends as `x-workspace-id` on every backend
 * request, and which server pages read to scope their data.
 *
 * Also fetches pending invitations client-side and surfaces them inline with
 * Accept buttons, so users already in the app can join other workspaces without
 * going through the /onboarding screen.
 */
export function CustomerSwitcher({
  workspaces,
  activeId,
}: {
  workspaces: Workspace[];
  activeId: string | null;
}) {
  const revalidate = useRevalidate();

  // Pending invitations — fetched client-side so server render doesn't block.
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Map of workspaceId → per-invite error string.
  const [acceptErrors, setAcceptErrors] = useState<Record<string, string>>({});
  const [pending, startTransition] = useTransition();
  // Which invite's Accept button is active.
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMyInvitations()
      .then((list) => {
        if (!cancelled) setInvitations(list);
      })
      .catch(() => {
        if (!cancelled) setInviteError("Could not load invitations.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function setActive(id: string) {
    if (!id) return;
    writeActiveCustomerCookie(id);
    clearApiCache();
    revalidate();
  }

  function handleAccept(workspaceId: string) {
    setAcceptErrors((prev) => {
      const next = { ...prev };
      delete next[workspaceId];
      return next;
    });
    setBusyInviteId(workspaceId);
    startTransition(async () => {
      try {
        const result = await acceptInvitationAction(workspaceId);
        if (!result.ok) {
          setAcceptErrors((prev) => ({
            ...prev,
            [workspaceId]: result.error ?? "Could not accept invitation.",
          }));
          setBusyInviteId(null);
          return;
        }
        // Remove the accepted invite from the list.
        setInvitations((prev) => prev.filter((i) => i.workspaceId !== workspaceId));
        setBusyInviteId(null);
        // The server action already wrote the active-workspace cookie; write it
        // client-side too so it's immediately readable from document.cookie.
        setActive(result.workspaceId);
      } catch {
        setAcceptErrors((prev) => ({
          ...prev,
          [workspaceId]: "Something went wrong.",
        }));
        setBusyInviteId(null);
      }
    });
  }

  const active = workspaces.find((w) => w.id === activeId) ?? null;

  return (
    <div className="flex flex-col gap-1.5">
      {/* Workspace select row */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">
          Workspace
          {invitations.length > 0 && (
            <span
              className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-none text-white"
              title={`${invitations.length} pending invitation${invitations.length === 1 ? "" : "s"}`}
            >
              {invitations.length}
            </span>
          )}
        </label>
        <select
          value={activeId ?? ""}
          onChange={(e) => setActive(e.target.value)}
          className="rounded border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 dark:border-border dark:bg-surface dark:text-zinc-100"
        >
          {activeId === null && (
            <option value="" disabled>
              Select
            </option>
          )}
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        {active && (
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 dark:bg-surface-2 dark:text-zinc-400">
            {roleLabel(active.role)}
          </span>
        )}
      </div>

      {/* Pending invitations */}
      {inviteError && (
        <p className="text-xs text-red-500 dark:text-red-400">{inviteError}</p>
      )}
      {invitations.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/20">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            Pending invitation{invitations.length === 1 ? "" : "s"}
          </p>
          <ul className="flex flex-col gap-1">
            {invitations.map((inv) => (
              <li
                key={inv.workspaceId}
                className="flex items-center justify-between gap-3"
              >
                <span className="text-xs text-zinc-700 dark:text-zinc-300">
                  <span className="font-medium">{inv.workspaceName}</span>
                  <span className="ml-1 text-zinc-500 dark:text-zinc-400">
                    as {roleLabel(inv.workspaceRole)}
                  </span>
                </span>
                <div className="flex flex-col items-end gap-0.5">
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => handleAccept(inv.workspaceId)}
                    className="inline-flex items-center gap-1 rounded border border-amber-400 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-700 dark:bg-surface dark:text-amber-300 dark:hover:bg-amber-950/30"
                  >
                    {busyInviteId === inv.workspaceId ? (
                      <SpinnerIcon />
                    ) : null}
                    Accept
                  </button>
                  {acceptErrors[inv.workspaceId] && (
                    <span className="text-[10px] text-red-500 dark:text-red-400">
                      {acceptErrors[inv.workspaceId]}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="animate-spin shrink-0"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
